import { GEWERK } from './gemeinsam';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  StatusBadge, type Spalte,
} from '../../muster';

/**
 * Lösungen — Anweisungen aus der Loesungsableitung. Jeder Ausweichvorschlag
 * wird eine konkrete Anweisung: Gleichrangregel, Rollentausch bei
 * Steigleitungen, Durchbruch oder — wo im Deckenhohlraum kein Gewerk mehr
 * ausweichen kann — eine gebündelte Planeraufgabe.
 */
export function Loesungen({
  loesungen, ableitetLaeuft, onAbleiten, fehler,
}: {
  loesungen: any;
  ableitetLaeuft: boolean;
  onAbleiten: () => void;
  fehler?: string;
}) {
  const artSpalten: Spalte<any>[] = [
    {
      schluessel: 'art', titel: 'Lösungsart', immerSichtbar: true,
      wert: (l) => l.bezeichnung ?? l.schluessel,
    },
    {
      schluessel: 'zustaendig', titel: 'Zuständig', breite: '150px',
      wert: (l) => l.zustaendig,
      inhalt: (l) => <span className="schwach">{l.zustaendig}</span>,
    },
    { schluessel: 'anzahl', titel: 'Anzahl', art: 'zahl', wert: (l) => l.anzahl },
    {
      schluessel: 'anteil', titel: 'Anteil', art: 'zahl', einheit: '%',
      nachkomma: 0, wert: (l) => (l.anteil ?? 0) * 100,
    },
  ];

  const paarSpalten: Spalte<any>[] = [
    {
      schluessel: 'gewerk', titel: 'Gewerk', immerSichtbar: true,
      wert: (g) => GEWERK[g.gewerk] ?? g.gewerk,
    },
    {
      schluessel: 'gegen', titel: 'Gegengewerk',
      wert: (g) => GEWERK[g.gegenGewerk] ?? g.gegenGewerk ?? '',
      inhalt: (g) => GEWERK[g.gegenGewerk] ?? g.gegenGewerk ?? '—',
    },
    {
      schluessel: 'loesungsart', titel: 'Lösungsart',
      wert: (g) => g.loesungsart,
      inhalt: (g) => <span className="schwach">{g.loesungsart}</span>,
    },
    { schluessel: 'anzahl', titel: 'Anzahl', art: 'zahl', wert: (g) => g.anzahl },
  ];

  const freigabeSpalten: Spalte<any>[] = [
    {
      schluessel: 'gewerk', titel: 'Gewerk', immerSichtbar: true,
      wert: (f) => GEWERK[f.gewerk] ?? f.gewerk ?? '',
      inhalt: (f) => GEWERK[f.gewerk] ?? f.gewerk ?? '—',
    },
    {
      schluessel: 'gegen', titel: 'Gegengewerk',
      wert: (f) => GEWERK[f.gegenGewerk] ?? f.gegenGewerk ?? '',
      inhalt: (f) => GEWERK[f.gegenGewerk] ?? f.gegenGewerk ?? '—',
    },
    {
      schluessel: 'freigegeben', titel: 'Freigegeben', art: 'status', breite: '140px',
      wert: (f) => (f.istFreigegeben ? 'ja' : 'nein'),
      inhalt: (f) => (f.istFreigegeben
        ? <StatusBadge zustand="gut" text="ja" klein />
        : <StatusBadge zustand="neutral" text="nein" klein />),
    },
    {
      schluessel: 'quote', titel: 'Quote', art: 'zahl', einheit: '%', nachkomma: 0,
      wert: (f) => (f.quoteBeiFreigabe != null ? f.quoteBeiFreigabe * 100 : null),
    },
    {
      schluessel: 'faelle', titel: 'Fälle', art: 'zahl',
      wert: (f) => f.faelleBeiFreigabe,
    },
  ];

  return (
    <ContentSection
      titel="Lösungen"
      beschreibung={'Aus jedem Ausweichvorschlag eine konkrete Anweisung: '
        + 'Gleichrangregel, Rollentausch bei Steigleitungen, Durchbruch oder — wo im '
        + 'Deckenhohlraum kein Gewerk mehr ausweichen kann — eine gebündelte '
        + 'Planeraufgabe.'}
      aktionen={(
        <button className="m-knopf m-knopf-haupt" onClick={onAbleiten}
                disabled={ableitetLaeuft}>
          {ableitetLaeuft ? 'leitet ab …' : 'Lösungen ableiten'}
        </button>
      )}
      nackt
    >
      {fehler && (
        <ErrorState
          meldung="Die Lösungen konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={onAbleiten}
        />
      )}

      {!loesungen && !fehler && (
        <EmptyState
          titel="Noch keine Lösung abgeleitet."
          erklaerung={'Die Ableitung macht aus jedem Ausweichvorschlag eine Anweisung — '
            + 'sie braucht einen Kollisionslauf als Grundlage.'}
          aktion={(
            <button className="m-knopf m-knopf-haupt" onClick={onAbleiten}
                    disabled={ableitetLaeuft}>
              {ableitetLaeuft ? 'leitet ab …' : 'Lösungen ableiten'}
            </button>
          )}
        />
      )}

      {loesungen && (
        <>
          <KpiReihe>
            <KpiCard
              titel="Automatisch gelöst"
              wert={loesungen.quoteAutomatisch != null
                ? Math.round(loesungen.quoteAutomatisch * 100) : null}
              einheit="%"
              vergleich={`${(loesungen.kollisionen ?? 0).toLocaleString('de-DE')} echte Kollisionen`}
            />
            <KpiCard
              titel="An Planer gebündelt"
              wert={loesungen.quotePlaner != null
                ? Math.round(loesungen.quotePlaner * 100) : null}
              einheit="%"
            />
            <KpiCard titel="Mit Anweisung" wert={loesungen.mitAnweisung} />
          </KpiReihe>

          <DataTable
            name="Je Lösungsart"
            spalten={artSpalten}
            zeilen={(loesungen.jeLoesungsart ?? []) as any[]}
            schluessel={(l: any) => l.schluessel}
            sortierung={{ spalte: 'anzahl', richtung: 'ab' }}
            ausfuhr
            ausfuhrDateiname="Loesungsarten"
          />

          <DataTable
            name="Je Gewerkpaar"
            spalten={paarSpalten}
            zeilen={(loesungen.jeGewerkpaar ?? []) as any[]}
            schluessel={(g: any) => `${g.gewerk}|${g.gegenGewerk}|${g.loesungsart}`}
            sortierung={{ spalte: 'anzahl', richtung: 'ab' }}
            ausfuhr
            ausfuhrDateiname="Gewerkpaare"
          />

          <DataTable
            name="Automatik-Freigaben"
            spalten={freigabeSpalten}
            zeilen={(loesungen.freigaben ?? []) as any[]}
            schluessel={(f: any) => `${f.gewerk}|${f.gegenGewerk}`}
            ausfuhr
            ausfuhrDateiname="Automatik-Freigaben"
            leer={(
              <EmptyState
                titel="Keine Automatik freigeschaltet."
                erklaerung={'Freigegeben wird unter „Nächtlicher Lauf" — erst über der '
                  + 'Mindestquote und ab einer Mindestzahl an Fällen.'}
              />
            )}
          />
        </>
      )}
    </ContentSection>
  );
}
